import React from 'react'
import { useTranslation } from 'react-i18next'
import { animated } from 'react-spring'
import css from 'styled-jsx/css'
import { useDropDown } from '~/hooks/dropdown'
import { useUser } from '~/hooks/server/sync'

import Organization from '~/models/organization'

import { ChildProps } from '~/common/define/props'
import { organizationBasePath, organizationNewPath, spaceSettingPath } from '~/common/define/path'
import { OrganizationColor } from '~/common/define/color'
import { addClass } from '~/common/utils/html'
import RowList from '~/components/atoms/row/RowList'
import Row from '~/components/atoms/row/Row'
import RowColumnLayout, { RowColumnLayoutFrame } from '~/components/molecules/row/content/RowColumnLayout'
import ButtonBase, { ButtonRole, ButtonSize } from '~/components/atoms/button/ButtonBase'
import ModelsIcon from '~/components/atoms/icon/ModelsIcon'
import RowLink from '~/components/atoms/row/RowLink'
import RowSimpleLayout from '~/components/molecules/row/content/RowSimpleLayout'
import { IconColor, IconFrame, IconSize } from '~/components/atoms/icon/Icon'
import TooltipWrapper from '~/components/wrapper/tooltip/TooltipWrapper'
import { isAdminAccessibleOrganization } from '~/common/define/access'

interface Props extends ChildProps {
  organization: Organization
}

const OrganizationDropdown = ({children, organization}: Props) => {
  const {t} = useTranslation()
  const user = useUser()
  const {clickableRef, onClickOpen, open, dropDownStyles, dropDownClass} = useDropDown()
  const isAdmin = isAdminAccessibleOrganization(user, organization)

  const {className: sizeClass, styles: sizeStyles} = css.resolve`
    div { width: 320px; }
  `

  return (
    <div onClick={onClickOpen} ref={clickableRef}>
      <TooltipWrapper text={t('header:tooltip.organization')} off={open}>
        {children}
      </TooltipWrapper>
      {dropDownStyles && user && organization &&
        <animated.div style={dropDownStyles} className={dropDownClass + addClass(sizeClass)}>
          <RowList noBorder>
            <Row>
              <RowColumnLayout frame={RowColumnLayoutFrame.SMALL} current
                               subNode={
                                 isAdmin ?
                                   <ButtonBase role={ButtonRole.SUPPORT} color={OrganizationColor.ORGANIZATION}
                                               size={ButtonSize.SMALL} link={spaceSettingPath(organization)}>
                                     {t('header:org.management')}
                                   </ButtonBase> : null}
              >
                <ModelsIcon
                  image={organization.icon_image}
                  tag={organization.icon_tag}
                  frame={IconFrame.SQUARE}
                  fillColor={IconColor.WHITE}
                  size={IconSize.M_LARGE}
                />
                {organization.name}
              </RowColumnLayout>
            </Row>
            {user.organizations && user.organizations.map(loopOrganization =>
              loopOrganization.id != organization.id &&
              <Row key={loopOrganization.id} clickable>
                <RowLink link={organizationBasePath(loopOrganization)}>
                  <RowColumnLayout>
                    <ModelsIcon image={loopOrganization.icon_image} tag={loopOrganization.icon_tag}/>
                    {loopOrganization.name}
                  </RowColumnLayout>
                </RowLink>
              </Row>
            )}
            <Row>
              <RowSimpleLayout>
                <ButtonBase role={ButtonRole.MODAL_ACTION} color={OrganizationColor.ORGANIZATION} link={organizationNewPath()} noShrink>
                  {t('header:org.create')}
                </ButtonBase>
              </RowSimpleLayout>
            </Row>
          </RowList>
          {sizeStyles}
        </animated.div>
      }
    </div>
  )
}

export default OrganizationDropdown
